import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AI_MODEL_SLOTS, AI_MODEL_SLOT_INFO, aiModelSlotFeatures } from '@mg/shared';
import type { AiDeploymentsView, AiModelSlot, AiModelsView, UpdateAiModelsDto } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useIsTenantAdmin } from '../../auth/useWorkspaceAdmin';
import { useBrandWorkspaceV2Known } from '../brand/useBrandFlags';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { CHANGES_SAVED, useToast } from '../../components/ui/toast';
import { useFooterSave, useSaveHold } from '../../components/settings/footerSave';
import { DESIGN_TOOLS_QUERY_KEY } from '../media/useDesignTools';
import {
  AI_MODELS_QUERY_KEY,
  AI_MODEL_DEPLOYMENTS_QUERY_KEY,
  INSTALL,
  OFF,
  buildUpdate,
  currentValue,
  deploymentDetails,
  draftWarnings,
  embeddingsReadOnlyText,
  embeddingsSelectable,
  installLabel,
  optionLabel,
  rebuildBody,
  sourceText,
  type AiModelDrafts,
} from './aiModels';

/**
 * App Settings → AI models: which Azure AI deployment each AI feature uses on THIS install.
 *
 * Tenant-Admin-only, and HIDDEN for everyone else: its endpoints 403 for them, so neither query runs and
 * nothing renders (owner ruling O3; the server enforces it on every route). Install-wide, so it needs no
 * Brand Workspace.
 *
 * Two reads. The models in force never ask Azure, so they show even while Azure can't be asked. The
 * install's live deployment list comes from Azure in its own request after mount, and every dropdown is
 * built from it: there is no free text anywhere (O2). Every change except switching image generation off
 * needs that list, because the server checks each one against it before saving (D4).
 *
 * A pick not saved yet is a draft. An absent draft shows the server's value (as CustomerDirectoryCard
 * does), so a refetch or another admin's save is never hidden behind a stale local copy, and after a save
 * the card shows the server's answer rather than what was picked.
 *
 * On App Settings it is its own tab, and the page's sticky Save footer saves the drafts: the card's own
 * Save stands down there. Refresh list, "Build requests for" and Discard changes stay in the card.
 */
export function AiModelsCard() {
  const isTenantAdmin = useIsTenantAdmin();
  const queryClient = useQueryClient();
  const toast = useToast();
  // Leaving the tab or the page waits for a "Build requests for" still running, so its answer shows.
  const hold = useSaveHold();
  const view = useQuery({
    queryKey: AI_MODELS_QUERY_KEY,
    queryFn: ({ signal }) => api.getAiModels(signal),
    enabled: isTenantAdmin,
  });
  // Its own request, so the models in force never wait for Azure.
  const deployments = useQuery({
    queryKey: AI_MODEL_DEPLOYMENTS_QUERY_KEY,
    queryFn: ({ signal }) => api.getAiModelDeployments(signal),
    enabled: isTenantAdmin,
  });
  const [drafts, setDrafts] = useState<AiModelDrafts>({});
  const [error, setError] = useState<string | null>(null);
  const idBase = useId();
  // Brand Setup and the legacy migration run on the Content Composer slot only where Brand Workspace v2 is on.
  const brandWorkspaceV2 = useBrandWorkspaceV2Known();

  const onSaved = (next: AiModelsView) => {
    setError(null);
    // The server's answer as it stands: no refetch, and never the draft.
    queryClient.setQueryData(AI_MODELS_QUERY_KEY, next);
    // Problems and warnings follow the new values.
    void queryClient.invalidateQueries({ queryKey: AI_MODEL_DEPLOYMENTS_QUERY_KEY });
    // The change is in Site Integrations → Recent site changes.
    void queryClient.invalidateQueries({ queryKey: ['site-audit'] });
    // Whether Generate media offers the built-in generator (D3): the hook's own key, not a copy of it.
    void queryClient.invalidateQueries({ queryKey: DESIGN_TOOLS_QUERY_KEY });
  };
  // A refusal (400), Azure out of reach (503) and an installation on hold (403) all say why, verbatim.
  const onError = (err: unknown) =>
    setError(err instanceof ApiRequestError ? err.message : 'Could not save the AI models.');
  const save = useMutation({
    mutationFn: (body: UpdateAiModelsDto) => api.updateAiModels(body),
    onSuccess: (next) => {
      setDrafts({});
      onSaved(next);
    },
    onError,
  });
  // "Build requests for {model}": the deployment stored here, sent again so the server re-reads its model.
  // Picks in other slots stay drafted. It saves at once, so it confirms with the toast as a Save does.
  const rebuild = useMutation({
    mutationFn: ({ body }: { slot: AiModelSlot; body: UpdateAiModelsDto }) => api.updateAiModels(body),
    onSuccess: (next) => {
      onSaved(next);
      toast.show(CHANGES_SAVED);
    },
    onError,
  });
  const busy = save.isPending || rebuild.isPending;
  // The list as Azure last answered it; nothing while it loads or when it can't be read.
  const list = deployments.isSuccess ? deployments.data : undefined;
  const current = view.data;
  const dirty = current !== undefined && Object.keys(buildUpdate(drafts, current)).length > 0;

  /** Saves every pick not saved yet; resolves to whether it worked (a failure says why in the card). */
  const saveDrafts = async (): Promise<boolean> => {
    // No pick to send: nothing can fail.
    if (current === undefined || !dirty) return true;
    setError(null);
    try {
      await save.mutateAsync(buildUpdate(drafts, current));
      return true;
    } catch {
      return false;
    }
  };
  const discardDrafts = () => {
    setError(null);
    setDrafts({});
  };
  // The page's sticky Save footer, where there is one. While "Build requests for" runs, the footer waits
  // for it, as the card's own Save does.
  const { inFooter } = useFooterSave({
    id: 'ai-models',
    dirty,
    saving: rebuild.isPending,
    save: saveDrafts,
    discard: discardDrafts,
  });

  const pick = (slot: AiModelSlot, value: string) => {
    setError(null);
    // A new pick starts a new change: "Saved." was about the last one.
    save.reset();
    rebuild.reset();
    setDrafts((prev) => {
      const next = { ...prev };
      if (current && value === currentValue(current.slots[slot])) delete next[slot];
      else next[slot] = value;
      return next;
    });
  };

  // After every hook, so the hook order is the same whatever the role.
  if (!isTenantAdmin) return null;

  return (
    <Card>
      <CardHeader
        title="AI models"
        description={describeCard(current?.endpointHost ?? null)}
        action={
          <Button
            variant="secondary"
            size="sm"
            loading={deployments.isFetching}
            disabled={busy}
            onClick={() => void deployments.refetch()}
          >
            Refresh list
          </Button>
        }
      />
      <CardBody className="space-y-5">
        {view.isLoading ? (
          <p className="text-sm text-ink-faint">Loading the AI models…</p>
        ) : view.isError || !current ? (
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load the AI models.</p>
        ) : (
          <>
            {/* Stays mounted while its line changes, so what Azure answered is announced: a failed Refresh
                list replaces the line while the button that asked is disabled. */}
            <div aria-live="polite" aria-atomic="true">
              {deployments.isError ? (
                <p className="text-sm text-red-600 dark:text-error-dark">{listErrorText(deployments.error)}</p>
              ) : list ? (
                <p className="text-sm text-ink-muted">
                  Azure AI account <strong className="font-semibold text-ink">{list.account.name}</strong> ·{' '}
                  {countDeployments(list.deployments.length)}
                </p>
              ) : (
                <p className="text-sm text-ink-faint">Loading this install&apos;s deployments from Azure…</p>
              )}
            </div>

            {!current.aiConfigured && (
              <p className="text-sm text-amber-700 dark:text-warning-dark">
                AZURE_OPENAI_ENDPOINT isn&apos;t set on this install, so AI features return placeholder text.
                Choices saved here take effect once it is.
              </p>
            )}

            <div className="space-y-4">
              {AI_MODEL_SLOTS.map((slot) => (
                <SlotPanel
                  key={slot}
                  slot={slot}
                  features={aiModelSlotFeatures(slot, brandWorkspaceV2)}
                  view={current}
                  list={list}
                  draft={drafts[slot]}
                  busy={busy}
                  rebuilding={rebuild.isPending && rebuild.variables?.slot === slot}
                  idBase={idBase}
                  onPick={(value) => pick(slot, value)}
                  onRebuild={() => {
                    setError(null);
                    void hold(rebuild.mutateAsync({ slot, body: rebuildBody(slot, current) }));
                  }}
                />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {!inFooter && (
                <Button
                  loading={save.isPending}
                  disabled={!dirty || busy}
                  onClick={() => {
                    if (!dirty) return;
                    void saveDrafts().then((ok) => {
                      if (ok) toast.show(CHANGES_SAVED);
                    });
                  }}
                >
                  Save
                </Button>
              )}
              <Button variant="secondary" disabled={!dirty || busy} onClick={discardDrafts}>
                Discard changes
              </Button>
              {/* Always mounted, and only its text changes: a status region added with its text already in
                  it isn't reliably announced. */}
              <span role="status" className="text-sm text-ink-muted">
                {(save.isSuccess || rebuild.isSuccess) && !dirty ? 'Saved.' : ''}
              </span>
              {error && (
                <span role="alert" className="text-sm text-red-600 dark:text-error-dark">
                  {error}
                </span>
              )}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/** The card's description, naming the endpoint's host so two open tabs on two installs can't be confused. */
function describeCard(endpointHost: string | null): string {
  const where = endpointHost ? ` (${endpointHost})` : '';
  return (
    `Which Azure AI deployment each AI feature uses on this install${where}. A choice here wins over the ` +
    "install's setting and applies to the next request on every server, with no deploy. Every change is " +
    "checked against this install's deployments in Azure first. Each install is set separately, and every " +
    'change is listed under Site Integrations → Recent site changes.'
  );
}

/** Why the list is missing: the server's own sentence (it never carries Azure's raw message), then what still works. */
function listErrorText(err: unknown): string {
  const said = err instanceof ApiRequestError && err.message.trim() !== '' ? err.message.trim() : null;
  const reason = said === null ? '.' : `: ${/[.!?]$/.test(said) ? said : `${said}.`}`;
  return (
    `Couldn't load the deployments from Azure${reason} The models in force are shown below. ` +
    'Changes need this list, except switching image generation off.'
  );
}

const countDeployments = (n: number) => `${n} ${n === 1 ? 'deployment' : 'deployments'}`;

interface SlotPanelProps {
  slot: AiModelSlot;
  /** What the slot drives on this install (aiModelSlotFeatures). */
  features: readonly string[];
  view: AiModelsView;
  /** The deployment list, once Azure has answered; undefined while it loads or when it can't be read. */
  list: AiDeploymentsView | undefined;
  /** What the admin picked here and has not saved; undefined = the server's value. */
  draft: string | undefined;
  /** A save or a rebuild is running: every control waits for it. */
  busy: boolean;
  /** This slot's "Build requests for" is the one running. */
  rebuilding: boolean;
  idBase: string;
  onPick: (value: string) => void;
  onRebuild: () => void;
}

/** One AI feature group: what runs it now and why, what is wrong with that, and the dropdown to change it. */
function SlotPanel({ slot, features, view, list, draft, busy, rebuilding, idBase, onPick, onRebuild }: SlotPanelProps) {
  const info = AI_MODEL_SLOT_INFO[slot];
  const slotView = view.slots[slot];
  const slotList = list?.slots[slot];
  const titleId = `${idBase}-${slot}-title`;
  const inForce =
    slotView.effective === null ? undefined : list?.deployments.find((d) => d.name === slotView.effective);
  // What Azure reports for it; until the list is in, the model it was chosen for, if it was chosen here.
  const details = inForce ? deploymentDetails(inForce) : list ? null : slotView.modelName;
  const problem = slotList?.currentProblem ?? null;
  const currentWarnings = slotList?.currentWarnings ?? [];
  // A stored deployment whose model Azure now reports differently: one click re-reads it. Only where the
  // server would accept that save, i.e. it still offers the deployment for this slot (`options` is exactly
  // what a PATCH accepts). Otherwise every click would be refused; "Not offered here" says why.
  const canRebuild =
    problem?.code === 'model-changed' &&
    Boolean(problem.liveModel) &&
    slotView.override !== null &&
    (slotList?.options.some((option) => option.name === slotView.override) ?? false);

  return (
    <div role="group" aria-labelledby={titleId} className="rounded-xl border border-line-soft p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p id={titleId} className="text-sm font-semibold text-ink">
          {info.label}
        </p>
        <Badge tone={slotView.source === 'database' ? 'brand' : 'neutral'}>{sourceText(slot, view)}</Badge>
      </div>
      {features.length > 0 && <p className="mt-1 text-xs text-ink-faint">Used by: {features.join(' · ')}</p>}
      {info.note && <p className="mt-1 text-xs text-ink-faint">{info.note}</p>}

      <p className="mt-3 text-sm text-ink">
        In use now: <span className="font-mono">{slotView.effective ?? 'Off'}</span>
        {details && <span className="text-ink-muted"> — {details}</span>}
      </p>
      {problem && (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p className="text-sm text-red-600 dark:text-error-dark">{problem.text}</p>
          {canRebuild && (
            <Button variant="secondary" size="sm" loading={rebuilding} disabled={busy} onClick={onRebuild}>
              Build requests for {problem.liveModel}
            </Button>
          )}
        </div>
      )}
      {currentWarnings.length > 0 && (
        <ul className="mt-2 space-y-1">
          {currentWarnings.map((warning, i) => (
            <li key={i} className="text-sm text-amber-700 dark:text-warning-dark">
              {warning}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3">
        {slot === 'embeddings' && !embeddingsSelectable(view, list) ? (
          // Owner ruling O1: Embeddings changes only to a compatible, more capable deployment.
          <p className="text-sm text-ink-muted">{embeddingsReadOnlyText(view, list)}</p>
        ) : (
          <SlotSelect
            slot={slot}
            view={view}
            list={list}
            draft={draft}
            busy={busy}
            idBase={idBase}
            onPick={onPick}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The slot's dropdown: the install's real deployments only, never free text (O2), so `Select` is used
 * without `searchable` or `allowCustomValue`. Every option is one the server accepts for this slot; the
 * server checks again on save.
 */
function SlotSelect({
  slot,
  view,
  list,
  draft,
  busy,
  idBase,
  onPick,
}: Pick<SlotPanelProps, 'slot' | 'view' | 'list' | 'draft' | 'busy' | 'idBase' | 'onPick'>) {
  const slotView = view.slots[slot];
  const slotList = list?.slots[slot];
  const current = currentValue(slotView);
  const selectId = `${idBase}-${slot}-deployment`;
  const warningsId = `${idBase}-${slot}-warnings`;

  const installIsCurrent = current === INSTALL;
  // Embeddings offers the way back only where it leads somewhere: a deployment is stored here (O1).
  const showInstall = slot !== 'embeddings' || installIsCurrent || slotView.override !== null;
  const installProblem = slotList?.install.problem ?? null;
  // Going back is checked like any other change, so it needs the list, and the server must accept it.
  const installDisabled = !installIsCurrent && (!list || installProblem !== null);
  const options = slotList?.options ?? [];
  // The deployment stored here, when the list can't offer it (not loaded, or no longer eligible): shown,
  // so the dropdown still says what is chosen, and never chosen again once the list is in without it.
  const storedOutsideList =
    slotView.override !== null && !options.some((option) => option.name === slotView.override)
      ? slotView.override
      : null;
  const notes = draftWarnings(slot, draft, view, list);

  return (
    <>
      <label htmlFor={selectId} className="mb-1 block text-xs font-medium text-ink-muted">
        Deployment
      </label>
      <Select
        id={selectId}
        value={draft ?? current}
        // Without the list only switching image generation off can be saved.
        disabled={busy || (!list && slot !== 'image')}
        aria-describedby={notes.length > 0 ? warningsId : undefined}
        onChange={(e) => onPick(e.target.value)}
      >
        {showInstall && (
          <option
            value={INSTALL}
            disabled={installDisabled}
            title={installDisabled ? (installProblem ?? "Needs this install's deployment list from Azure") : undefined}
          >
            {installLabel(slot, view)}
          </option>
        )}
        {slot === 'image' && <option value={OFF}>Off: image generation switched off here</option>}
        {options.map((option) => {
          const deployment = list?.deployments.find((d) => d.name === option.name);
          return (
            <option key={option.name} value={option.name}>
              {deployment ? optionLabel(deployment) : option.name}
            </option>
          );
        })}
        {storedOutsideList !== null && (
          <option value={storedOutsideList} disabled={list !== undefined}>
            {`${storedOutsideList} (set here)`}
          </option>
        )}
      </Select>
      {showInstall && installDisabled && installProblem !== null && (
        <p className="mt-1 text-xs text-ink-faint">Use install setting isn&apos;t available: {installProblem}</p>
      )}
      {notes.length > 0 && (
        <ul id={warningsId} className="mt-2 space-y-1">
          {notes.map((note, i) => (
            <li key={i} className="text-sm text-amber-700 dark:text-warning-dark">
              {note}
            </li>
          ))}
        </ul>
      )}
      {slotList && slotList.excluded.length > 0 && (
        <p className="mt-2 text-xs text-ink-faint">
          Not offered here: {slotList.excluded.map((e) => `${e.name} (${e.reason})`).join('; ')}
        </p>
      )}
    </>
  );
}
